import { useEffect, useState } from 'react';
import { Tooltip } from 'antd';

import If from 'global/ErrorHandlers/If';
import Image from 'global/Image';

import useMultiPortfolioStore from 'lib/stores/multiPorto';
import useSubAccounts from 'features/multi-porto/hooks/useSubAccounts';
import useSwitchAction from 'features/multi-porto/hooks/useSwitchAction';
import useSecuritiesCredentialStore from 'global/Securities/stores/useSecuritiesCredentialStore';
import useModalStore, {
  ModalNames,
} from 'features/multi-porto/stores/modal.store';
import { SubAccountStatus } from 'global/Securities/types/api/multiPorto';

import {
  MenuText,
  ItemTextName,
  ButtonSettingsContent,
  SectionContainer,
  ButtonSwitchItem,
  ButtonSwitchItemAction,
  ImageContainer,
  ButtonRename,
  TooltipContent,
  TooltipText,
  Divider,
} from './styled';

interface MenuSwitchContentProps {
  hideTransfer?: boolean;
  width: number;
  source: string;
  onClick: () => void;
  onClose?: () => void;
  onSuccessSwitch: (accountNumber: string) => void;
}

export default function MenuSwitchContent({
  hideTransfer = false,
  width = 0,
  source,
  onClick,
  onClose = () => {},
  onSuccessSwitch,
}: Readonly<MenuSwitchContentProps>) {
  const [isEllipsis, setIsEllipsis] = useState<Record<string, boolean>>({});

  const activeAccount = useMultiPortfolioStore((s) => s.activeAccount);

  const { subAccounts, approvedSubAccounts } = useSubAccounts();
  const subAccountsLength = approvedSubAccounts.length;

  const setModal = useModalStore((s) => s.openModal);
  const { onOpenTransferCash, onOpenTransferStock, onCreatePortfolio } =
    useSwitchAction();

  const setRenameForm = useMultiPortfolioStore((s) => s.setRenamePortfolioForm);
  const loadSwitchLoginReal = useSecuritiesCredentialStore(
    (s) => s.loadSwitchLoginReal,
  );

  const handleSelect = async (accountNumber: string) => {
    await loadSwitchLoginReal(accountNumber).then(() => {
      onClick();
      onSuccessSwitch(accountNumber);
    });
  };

  const handleClickRename = (accountNumber: string, portfolioName: string) => {
    onClick();
    onClose();
    setModal(ModalNames.PORTFOLIO_RENAME);
    setRenameForm({
      target_account_number: accountNumber,
      new_portfolio_name: portfolioName,
    });
  };

  const handleOpenTransferCash = () => {
    onClick();
    onOpenTransferCash();
  };

  const handleOpenTransferStock = () => {
    onClick();
    onOpenTransferStock();
  };

  const handleCreatePortfolio = () => {
    onClick();
    onClose();
    onCreatePortfolio();
  };

  const checkIfEllipsis = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      return element.offsetWidth < element.scrollWidth;
    }
    return false;
  };

  useEffect(() => {
    setTimeout(() => {
      const ellipsisStatus: Record<string, boolean> = {};
      subAccounts.forEach((portfolio) => {
        const accountNumber = portfolio.account_number;
        const elementId = `portfolio-menu-${accountNumber}`;
        ellipsisStatus[accountNumber] = checkIfEllipsis(elementId);
      });
      setIsEllipsis(ellipsisStatus);
    }, 500);
  }, [subAccounts.length]);

  if (subAccounts.length === 0) return null;

  return (
    <ButtonSettingsContent width={width} onClick={(e) => e.stopPropagation()}>
      <SectionContainer>
        {subAccounts.map((portfolio) => {
          const status = portfolio.account_status;
          const accountNumber = portfolio.account_number;
          const portfolioInfo = portfolio.portfolio_info;
          const portfolioName = portfolioInfo.portfolio_name;

          const isApproved = status === SubAccountStatus.APPROVED;
          const isPending = status === SubAccountStatus.PENDING;
          const isActive = accountNumber === activeAccount;

          return (
            <ButtonSwitchItem
              key={accountNumber + portfolioName}
              onClick={() => {
                if (!isApproved) return;

                if (!isActive) {
                  handleSelect(accountNumber);
                }
              }}
              style={{ cursor: isApproved ? 'pointer' : 'default' }}
            >
              <MenuText>
                <Tooltip
                  destroyTooltipOnHide
                  title={
                    isEllipsis[accountNumber] ? (
                      <TooltipText>
                        {portfolio.portfolio_info.portfolio_name}
                      </TooltipText>
                    ) : (
                      ''
                    )
                  }
                  placement="bottom"
                  arrowPointAtCenter={false}
                  color="#ffffff"
                  getTooltipContainer={(trigger) => trigger.parentElement}
                  overlayInnerStyle={{
                    marginTop: '-4px',
                  }}
                >
                  <ItemTextName
                    isMain={portfolio.is_main_account}
                    id={`portfolio-menu-${accountNumber}`}
                  >
                    {portfolio.portfolio_info.portfolio_name}
                  </ItemTextName>
                </Tooltip>

                <If condition={portfolio.is_main_account}>
                  <span className="main-porto"> • Main</span>
                </If>
              </MenuText>

              <If condition={isPending}>
                <Tooltip
                  destroyTooltipOnHide
                  title={
                    <TooltipText>
                      Portofolio baru masih dalam proses verifikasi
                    </TooltipText>
                  }
                  placement="bottomRight"
                  arrowPointAtCenter={false}
                  color="#ffffff"
                  getTooltipContainer={(trigger) => trigger.parentElement}
                  overlayStyle={{
                    minWidth: source === 'portfolio' ? '275px' : '295px',
                  }}
                  overlayInnerStyle={{
                    marginTop: '-4px',
                    marginRight: source === 'portfolio' ? '-20px' : '0',
                  }}
                >
                  <span className="pending-porto">In Verification</span>
                </Tooltip>
              </If>

              <If condition={isApproved}>
                <ImageContainer>
                  <If condition={isActive}>
                    <ButtonRename className="active-icon">
                      <Image
                        src="/icon/check/filled.svg"
                        alt=""
                        width={18}
                        height={18}
                      />
                    </ButtonRename>
                  </If>

                  <If condition={isApproved}>
                    <Tooltip
                      destroyTooltipOnHide
                      title={
                        <TooltipContent onClick={(e) => e.stopPropagation()}>
                          <TooltipText>Rename Portfolio</TooltipText>
                        </TooltipContent>
                      }
                      placement="bottom"
                      arrowPointAtCenter={false}
                      color="#ffffff"
                      getTooltipContainer={(trigger) => trigger.parentElement}
                      overlayInnerStyle={{
                        marginTop: '-4px',
                      }}
                    >
                      <ButtonRename
                        className={`rename-icon ${isActive ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClickRename(accountNumber, portfolioName);
                        }}
                      >
                        <Image
                          src="/icon/multi-porto/rename-porto.svg"
                          alt=""
                          width={16}
                          height={16}
                        />
                      </ButtonRename>
                    </Tooltip>
                  </If>
                </ImageContainer>
              </If>
            </ButtonSwitchItem>
          );
        })}

        <Divider height={1} style={{ margin: '8px 0' }} />

        <ButtonSwitchItemAction onClick={handleCreatePortfolio}>
          <Image src="/icon/plus/secondary.svg" alt="" width={16} height={16} />
          Create New Portfolio
        </ButtonSwitchItemAction>
      </SectionContainer>

      <If condition={!hideTransfer && subAccountsLength > 1}>
        <Divider height={4} />

        <SectionContainer>
          <ButtonSwitchItemAction onClick={handleOpenTransferCash}>
            <Image
              src="/icon/multi-porto/transfer-cash.svg"
              alt=""
              width={16}
              height={16}
            />
            <span>Transfer Cash</span>
          </ButtonSwitchItemAction>

          <ButtonSwitchItemAction onClick={handleOpenTransferStock}>
            <Image
              src="/icon/multi-porto/transfer-stock.svg"
              alt=""
              width={16}
              height={16}
            />
            <span>Transfer Stock</span>
          </ButtonSwitchItemAction>
        </SectionContainer>
      </If>
    </ButtonSettingsContent>
  );
}
